import { Link2 } from "lucide-react";
import { Card } from "@components/ui/card";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@components/ui/table";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#FF4567"];

export function Sources({
  data,
  loading,
  error,
  asTable,
  startDate,
  endDate,
}: {
  data: Record<string, any>[] | { [key: string]: any } | null | undefined;
  loading: boolean;
  error: string | null;
  asTable?: boolean;
  startDate?: Date;
  endDate?: Date;
}) {
  // Defensive: support both array and object API responses
  let sourceData = Array.isArray(data)
    ? data[0] && typeof data[0] === "object"
      ? data[0]
      : {}
    : data && typeof data === "object"
    ? data.data || data
    : {};

  // If the data is an array of signups, filter by date and aggregate counts
  if (Array.isArray(data) && data.length && data[0]?.signupDate) {
    const filtered = data.filter((row: any) => {
      const dateStr = row.signupDate;
      if (!dateStr) return true;
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return true;
      if (startDate && d < startDate) return false;
      if (endDate && d > endDate) return false;
      return true;
    });
    // Aggregate counts by source keys
    const agg: Record<string, number> = {};
    filtered.forEach((row: any) => {
      Object.keys(row).forEach((key) => {
        if (key.toLowerCase().endsWith("count") && Number(row[key]) > 0) {
          agg[key] = (agg[key] || 0) + Number(row[key]);
        }
      });
    });
    sourceData = agg;
  }

  // Prepare chart data: all keys ending with 'count' (case-insensitive) and value > 0
  const chartData = Object.entries(sourceData)
    .filter(
      ([key, value]) => key.toLowerCase().endsWith("count") && Number(value) > 0
    )
    .map(([key, value], idx) => ({
      name: key
        .replace(/Count$/i, "")
        .replace(/^[a-z]/, (c) => c.toUpperCase()),
      value: Number(value),
      color: COLORS[idx % COLORS.length],
    }));
  const total = chartData.reduce((sum, d) => sum + d.value, 0);

  return (
    <Card className="hover:shadow-lg transition-shadow duration-200 overflow-hidden p-6 border border-gray-800">
      <h2 className="text-lg font-semibold font-heading mb-4 flex items-center text-superone-charcoal">
        <Link2 className="mr-2 h-5 w-5 text-pink-500" />
        Signup Sources
      </h2>
      {loading ? (
        <div className="text-gray-400">Loading...</div>
      ) : error ? (
        <div className="text-red-500">{error}</div>
      ) : chartData.length > 0 ? (
        <>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{
                left: 20,
                right: 20,
                top: 10,
                bottom: 10,
              }}
            >
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" width={100} />
              <Tooltip
                formatter={(
                  value: number,
                  name: string,
                  props: any,
                  idx: number
                ) => [
                  `${value} (${((value / total) * 100).toFixed(1)}%)`,
                  chartData[idx].name,
                ]}
              />
              <Bar dataKey="value" isAnimationActive fill="#0088FE">
                {chartData.map((entry, idx) => (
                  <Cell key={`cell-${idx}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          {asTable && (
            <div className="overflow-x-auto mt-8">
              <Table className="rounded-md overflow-hidden border border-gray-200">
                <TableHeader>
                  <TableRow className="bg-[#0F87F2]">
                    <TableHead className="text-white text-base bg-[#0F87F2] select-none">
                      Source
                    </TableHead>
                    <TableHead className="text-white text-base bg-[#0F87F2] select-none text-right">
                      Count
                    </TableHead>
                    <TableHead className="text-white text-base bg-[#0F87F2] select-none text-right">
                      %
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {chartData.map((entry) => (
                    <TableRow key={entry.name}>
                      <TableCell>{entry.name}</TableCell>
                      <TableCell className="text-right">
                        {entry.value.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        {((entry.value / total) * 100).toFixed(1)}%
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell className="font-bold">Total</TableCell>
                    <TableCell className="text-right font-bold">
                      {total.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right font-bold">100%</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <div className="h-1 bg-[#0F87F2] w-full z-10" />
            </div>
          )}
        </>
      ) : (
        <div className="text-gray-400">No signup source data available</div>
      )}
    </Card>
  );
}
